// Auth provider with token-based authentication
// Tokens stored in cookies and sent via Authorization header

import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { authControllerMeV1, authControllerLogoutV1 } from "@/client/sdk.gen";
import { route, RouteKeys } from "@/services/routing/routes";
import queryClient from "@/services/react-query/query-client";
import { AuthContext, AuthActionsContext, AuthTokensContext } from "./auth-context";
import { User } from "./types";
import {
  getTokensInfo,
  setTokensInfo as setTokensInfoUtil,
  removeTokensInfo as removeTokensInfoUtil,
  subscribeToTokenChanges,
  TokensInfo,
} from "./auth-tokens";

/**
 * AuthProvider - Token-based authentication
 *
 * Key behaviors:
 * - Loads tokens from cookies on mount
 * - Loads user from /api/v1/auth/me using token in Authorization header
 * - Subscribes to token changes to reload user data
 * - Clears tokens and user on logout
 * - NO automatic token refresh (handled at request level)
 *
 * Backend responsibility:
 * - Returns { token, refreshToken, tokenExpires, user } on login
 * - Accepts Authorization: Bearer {token} header on authenticated endpoints
 */
export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  // Race condition guards
  const loggingOutRef = useRef(false);
  const isLoadingDataRef = useRef(false);

  /**
   * Load current user from /api/v1/auth/me
   * Uses Authorization header set by setTokensInfo
   */
  const loadUser = useCallback(async () => {
    // Block concurrent loadData calls
    if (isLoadingDataRef.current) {
      return;
    }

    isLoadingDataRef.current = true;

    try {
      // Check if tokens exist in cookies
      const tokens = getTokensInfo();
      if (!tokens?.token) {
        setUser(null);
        setIsLoaded(true);
        return;
      }

      // Apply tokens to API client headers (critical for page refresh!)
      setTokensInfoUtil(tokens, false);

      // Load user with token in header
      const { data, error } = await authControllerMeV1();

      // Only clear tokens on 401/403 (unauthorized)
      // Keep tokens on network errors (backend temporarily down)
      const isUnauthorized =
        error &&
        typeof error === "object" &&
        "status" in error &&
        (error.status === 401 || error.status === 403);

      if (isUnauthorized) {
        // Token invalid or expired - clear tokens
        setTokensInfoUtil(null, false);
        setUser(null);
        queryClient.clear();
      } else if (data) {
        // Success - set user
        setUser(data);
      }
      // If error but not 401/403, keep tokens (backend likely down)
    } catch {
      // Network error - keep tokens, backend might be temporarily down
      // User will stay "logged in" until backend comes back or token expires
    } finally {
      setIsLoaded(true);
      isLoadingDataRef.current = false;
    }
  }, []);

  /**
   * Logout: clear tokens, user, cache, then full-page navigate to sign-in.
   *
   * We use `window.location.assign` (a full reload) rather than React Router's
   * `navigate` to guarantee all in-memory state is reset:
   *   - React Router data-router cached loader results
   *   - TanStack Query in-flight observers / subscriptions
   *   - Stale closures captured by event handlers (e.g. a "Log in" link
   *     whose `to` was computed before logout completed)
   *
   * SPA-only navigation here previously left the app in a transitional state
   * where the public header re-rendered with a `<Link to="/sign-in">` that
   * pointed to the URL the user had just been pushed to, making the click a
   * no-op. A hard reload sidesteps that entire class of staleness bug and is
   * the right default for a logout — it's also security-positive (no cross-
   * session state leaks).
   */
  const logOut = useCallback(async () => {
    // Set guard to prevent token reload during logout
    loggingOutRef.current = true;

    // Call backend logout (best effort - ignore errors)
    try {
      await authControllerLogoutV1();
    } catch {
      // Ignore logout errors
    }

    // Explicit logout: clear tokens AND wipe the rememberMe preference so
    // the next sign-in starts from a clean slate. (`setTokensInfo(null)` is
    // also called from non-logout paths and intentionally preserves it.)
    removeTokensInfoUtil(false);

    // Clear user state
    setUser(null);

    // Clear all cached query data
    queryClient.clear();

    // Full-page navigation to sign-in. This intentionally bypasses the SPA
    // router so that no stale loader data, query observers, or closures
    // survive the logout. Note: code after this line will not run before the
    // browser tears the page down, so we deliberately do not reset
    // `loggingOutRef` — the flag is for any token-change listeners that fire
    // synchronously between here and the page unload.
    window.location.assign(route(RouteKeys.SIGN_IN));
  }, []);

  /**
   * Set tokens and update API client
   */
  const setTokensInfo = useCallback((tokens: TokensInfo | null) => {
    setTokensInfoUtil(tokens, true);
  }, []);

  /**
   * Load user on mount
   */
  useEffect(() => {
    loadUser();
  }, [loadUser]);

  /**
   * Subscribe to token changes
   * Reload user when tokens change (after login/refresh)
   */
  useEffect(() => {
    const unsubscribe = subscribeToTokenChanges((newTokens) => {
      if (loggingOutRef.current) {
        return; // Ignore during logout
      }

      if (newTokens?.token && !user) {
        // Tokens added, reload user
        loadUser();
      } else if (!newTokens?.token && user) {
        // Tokens cleared, clear user
        setUser(null);
      }
    });

    return unsubscribe;
  }, [user, loadUser]);

  /**
   * Multi-tab auth state sync
   * Re-verify auth state when tab becomes visible.
   * Throttled to at most once every 30 seconds to prevent 429s from rapid tab switching.
   */
  const lastVisibilityCheckRef = useRef<number>(0);

  useEffect(() => {
    const VISIBILITY_CHECK_INTERVAL_MS = 30_000;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible" && isLoaded) {
        // Throttle: skip if we checked recently
        const now = Date.now();
        if (now - lastVisibilityCheckRef.current < VISIBILITY_CHECK_INTERVAL_MS) {
          return;
        }
        lastVisibilityCheckRef.current = now;

        // Check if we still have tokens
        const tokens = getTokensInfo();
        if (tokens?.token) {
          // Re-verify auth state
          authControllerMeV1()
            .then(({ data, error }) => {
              // Only clear tokens on 401/403 (unauthorized)
              // Keep tokens on network errors (backend down)
              const isUnauthorized =
                error &&
                typeof error === "object" &&
                "status" in error &&
                (error.status === 401 || error.status === 403);

              if (isUnauthorized) {
                // User logged out in another tab or token expired
                setTokensInfoUtil(null, false);
                setUser(null);
                queryClient.clear();
              } else if (data) {
                // Update user in case data changed
                setUser(data);
              }
              // If error but not 401/403, keep current state (backend likely down)
            })
            .catch(() => {
              // Network error - keep tokens, don't sign out
              // Backend might be temporarily down
            });
        } else if (user) {
          // No tokens but we have user - clear user
          setUser(null);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isLoaded, user]);

  const contextValue = useMemo(
    () => ({
      user,
      isLoaded,
    }),
    [user, isLoaded]
  );

  const contextActionsValue = useMemo(
    () => ({
      setUser,
      logOut,
    }),
    [logOut]
  );

  const contextTokensValue = useMemo(
    () => ({
      setTokensInfo,
    }),
    [setTokensInfo]
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <AuthActionsContext.Provider value={contextActionsValue}>
        <AuthTokensContext.Provider value={contextTokensValue}>
          {children}
        </AuthTokensContext.Provider>
      </AuthActionsContext.Provider>
    </AuthContext.Provider>
  );
}
